import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { loginHref, useAuth } from '../lib/auth'
import { ConvergingDome } from '../components/art/ConvergingDome'
import { assetPath } from '../lib/assets'

/** Every refusal the callback can send back, in words. The codes are fixed on
 *  the server (services/auth.py) and deliberately say no more than this.
 *  `tenant` is the board's "Wrong organisation" state; the rest keep the
 *  sentences they had and are shown in the same block. */
const ERRORS: Record<string, { head: string; detail?: string }> = {
  state: { head: 'The sign-in took too long or was started in another tab. Try again.' },
  denied: { head: 'Microsoft did not complete the sign-in.' },
  tenant: {
    head: 'That account belongs to another organisation.',
    detail: 'Sign in with your hydrodiagnostics.ge address instead.',
  },
  disabled: { head: 'This account has been disabled. Ask an administrator.' },
  failed: { head: 'The sign-in could not be completed. Try again.' },
}

/** The two halves of the front door (SignIn board): the form on the left,
 *  the converging dome on the right, wrapping to one column on a narrow
 *  screen, where the dome is left out. Shared with the Pending page. */
export function SignInShell({ children }: { children: ReactNode }) {
  return (
    <div className="signin-page">
      <section className="signin-form" aria-labelledby="signin-title">
        {/* The full lockup belongs on a login screen (section 4.1). */}
        <img className="signin-logo" src={assetPath('brand/hd-logo.png')} width={737} height={343}
             alt="HydroDiagnostics, Engineering for safe environment" />
        <div className="signin-body">{children}</div>
        <div className="signin-foot">
          <span>© 2026 HydroDiagnostics LLC</span>
          <a href="https://hydrodiagnostics.ge">hydrodiagnostics.ge</a>
        </div>
      </section>
      <div className="signin-art" aria-hidden="true">
        <ConvergingDome />
      </div>
    </div>
  )
}

function MicrosoftMark() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 21 21" className="ms-mark">
      <rect x="0" y="0" width="10" height="10" fill="#F25022" />
      <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
      <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
      <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
  )
}

/** The front door. One button, because there is one way in. */
export function SignIn() {
  const [params] = useSearchParams()
  const { reason } = useAuth()
  const [busy, setBusy] = useState(false)
  const [demoError, setDemoError] = useState<string>()
  const isDemo = import.meta.env.VITE_DEMO_MODE === 'true'
  const error = params.get('error')
  const returnTo = params.get('return_to') ?? '/overview'
  const refusal = error ? ERRORS[error] ?? ERRORS.failed : null

  async function openDemo() {
    setBusy(true)
    setDemoError(undefined)
    try {
      const response = await fetch('/api/auth/demo-login', {
        method: 'POST', headers: { 'X-Requested-With': 'hydro' },
      })
      if (!response.ok) throw new Error('The demonstration workspace could not be opened. Reload and try again.')
      // A hash-only navigation keeps AuthProvider's signed-out state alive.
      // Reload after selecting the demo route so it reads the local session.
      window.location.replace('/#/overview')
      window.location.reload()
    } catch (error) {
      setDemoError(error instanceof Error ? error.message : 'The demonstration workspace could not be opened.')
      setBusy(false)
    }
  }

  return (
    <SignInShell>
      <div className="signin-title">
        <h1 id="signin-title">{isDemo ? 'Monitoring workspace' : 'Welcome back'}</h1>
        <p className="muted">{isDemo
          ? 'Explore the HydroDiagnostics interface with synthetic monitoring data.'
          : 'Sign in with your hydrodiagnostics.ge Microsoft account.'}</p>
      </div>

      {isDemo && demoError && <p role="alert" className="error">{demoError}</p>}
      {!isDemo && refusal && !busy && (
        <div role="alert" className="signin-alert">
          <p className="signin-alert-head">{refusal.head}</p>
          {refusal.detail && <p>{refusal.detail}</p>}
          {/* Straight to Microsoft's account picker: signing in again
              plainly would come back as the same wrong account. */}
          {error === 'tenant' && (
            <a className="signin-alert-link" href={loginHref(returnTo, true)}
               onClick={() => setBusy(true)}>
              Use another account
            </a>
          )}
        </div>
      )}
      {!refusal && reason && !busy && (
        <div role="status" className="signin-status"><p>{reason}</p></div>
      )}

      {/* A link, not a fetch: the flow is a chain of top-level redirects
          through Microsoft. The spinner covers the moment before the browser
          leaves the page. */}
      {isDemo ? (
        <button className="signin-button" type="button" onClick={() => void openDemo()}
                disabled={busy} aria-busy={busy}>
          {busy ? 'Opening workspace…' : 'Open demonstration workspace'}
        </button>
      ) : <a className="signin-button" href={loginHref(returnTo)} aria-busy={busy}
         onClick={() => setBusy(true)}>
        {busy
          ? <><span className="spinner" aria-hidden="true" /><span>Opening Microsoft sign-in…</span></>
          : <><MicrosoftMark /><span>Sign in with Microsoft</span></>}
      </a>}

      <p className="signin-small">
        {isDemo
          ? 'Demonstration only. Changes are saved in this browser. No live instruments, invitations or notifications are connected.'
          : 'New accounts wait for an administrator to approve them. Access from approved networks only.'}
      </p>
    </SignInShell>
  )
}
